import EntityTable from "@mirage/components/entityTable/EntityTable";
import { TrackedApplicationService } from "@publicPortal/services/TrackedApplicationService";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import { Filter, FilterItem } from "@shared/mirage/entityTable/Filter";
import { UserService } from "@publicPortal/services/UserService";
import React, { memo, useCallback, useMemo, useState } from "react";
import { IUser } from "@shared/mirage/core/contracts/IUser";
import { GridRenderCellParams } from "@mui/x-data-grid/models/params";
import ApplicationStatusCell from "@publicPortal/components/applicationStatusCell/ApplicationStatusCell";
import { GridColDef } from "@mui/x-data-grid-premium";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useStableNavigate } from "@mirage/hooks/useStableNavigate";
import { PublicPortalTheme } from "@publicPortal/theme/MuiPublicPortal.module";
import { faCirclePlay, faTrashXmark } from "@fortawesome/pro-regular-svg-icons";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import useAlertService from "@mirage/hooks/useAlertService";
import { TablePaginationBaseProps } from "@mui/material/TablePagination/TablePagination";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";

const trackedAppService = new TrackedApplicationService();
const pp = new PublicPortalRoutingService();

const TrackedApplicationsTab = memo(() => {
    const user: Partial<IUser> = UserService.getLoggedInUser();
    const navigate = useStableNavigate();
    const showAlert = useAlertService();
    const { isMobile } = useDeviceDetect();
    const [rowDeleted, setRowDeleted] = useState(false);
    const onPageRequest = useCallback(async (pageRequest: IEntityPageRequest) => await trackedAppService.pageRequest(pageRequest), []);
    const filter = useMemo(() => {
        const filter = { parts: [] } as Filter;
        const filterItems: FilterItem[] = [{ columnName: "userId", value: user?.id, operator: "=" }];
        filter.parts.push({ filterItems });
        return filter;
    }, [user?.id]);
    const isRowSelectable = useCallback(() => false, []);
    const handleDeleteRecord = useCallback(
        async (trackedId: number) => {
            const result = await trackedAppService.deleteTrackedApplication(trackedId);
            if (result.errorMsg) {
                showAlert(result.errorMsg, "error");
            } else {
                showAlert("You successfully untracked application", "success");
            }
            setRowDeleted((prev) => !prev);
        },
        [showAlert]
    );
    const getActions = useCallback(
        (trackedId: number, paId: number) => {
            return (
                <>
                    <Tooltip title="Go to record" placement="top">
                        <IconButton onClick={() => navigate(pp.PlanningApplicationRecordRoute(paId))}>
                            <FontAwesomeIcon icon={faCirclePlay} color={PublicPortalTheme.palette.success.main} style={PublicPortalTheme.icons.sizeM} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Track" placement="top">
                        <IconButton onClick={async () => await handleDeleteRecord(trackedId)}>
                            <FontAwesomeIcon icon={faTrashXmark} color={PublicPortalTheme.palette.error.main} style={PublicPortalTheme.icons.sizeM} />
                        </IconButton>
                    </Tooltip>
                </>
            );
        },
        [handleDeleteRecord, navigate]
    );
    const nameColumnDimensions = useMemo(() => (isMobile ? { width: 150, flex: undefined } : undefined), [isMobile]);
    const addressColumnDimensions = useMemo(() => (isMobile ? { width: 150, flex: undefined } : undefined), [isMobile]);
    const appTypeColumnDimensions = useMemo(() => (isMobile ? { width: 120, flex: undefined } : undefined), [isMobile]);
    const appStatusColumnDimensions = useMemo(() => (isMobile ? { width: 120, flex: undefined } : undefined), [isMobile]);
    const columns: GridColDef[] = useMemo(() => {
        return [
            {
                field: "actions",
                headerName: "Actions",
                renderCell: (params: GridRenderCellParams) => getActions(params.row.id, params.row.paId),
                sortable: false,
                resizable: false,
                groupable: false,
                width: 100,
            },
            { field: "name", headerName: "Application Reference", flex: 0.25, ...nameColumnDimensions },
            { field: "address", headerName: "Address", flex: 1, ...addressColumnDimensions },
            { field: "appTypeName", headerName: "Type", flex: 0.5, ...appTypeColumnDimensions },
            {
                field: "applicationStatus",
                headerName: "Status",
                flex: 0.25,
                renderCell: (params) => <ApplicationStatusCell applicationStatus={params.value} />,
                ...appStatusColumnDimensions,
            },
        ] as GridColDef[];
    }, [addressColumnDimensions, appStatusColumnDimensions, appTypeColumnDimensions, getActions, nameColumnDimensions]);
    const overridePaginationProps = useCallback((baseProps: TablePaginationBaseProps) => ({ ...baseProps, showFirstButton: false, showLastButton: false }), []);

    return (
        <EntityTable
            title="Tracked Applications"
            key="tracked-applications-entityTable"
            stateKey="tracked-applications-entityTable"
            onPageRequest={onPageRequest}
            columns={columns}
            filter={filter}
            forceFetch={rowDeleted}
            isRowSelectable={isRowSelectable}
            hideRowsPerPage={isMobile}
            overridePaginationProps={overridePaginationProps}
        />
    );
});

export default TrackedApplicationsTab;
